import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Box,
  Divider,
  Chip,
} from "@mui/material";
import {
  LocalAtm as CashIcon,
  CreditCard as CardIcon,
  Close as CloseIcon,
  Discount as DiscountIcon,
} from "@mui/icons-material";

const PaymentMethodDialog = ({
  open,
  onClose,
  onlineOnly = false,
  originalAmount,
  discountedAmount,
  paymentMethod,
  onPaymentMethodChange,
  onSubmit,
  isLoading,
  quantity,
}) => {
  const discountAmount = originalAmount - discountedAmount;
  const hasDiscount = discountAmount > 0;
  const unitPrice = originalAmount / quantity;
  const effectivePaymentMethod = onlineOnly ? "ONLINE" : paymentMethod;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          overflow: "visible",
          position: "relative",
        },
      }}
    >
      {/* Close button positioned at top-right corner of dialog */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "grey.500",
          zIndex: 1,
          "&:hover": { backgroundColor: "grey.100" },
        }}
        size="small"
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle className="p-6 pb-4 text-center">
        <Typography variant="h5" fontWeight="bold">
          Choose Payment Method
        </Typography>
      </DialogTitle>

      <DialogContent className="p-6 pt-0">
        {/* Price Breakdown */}
        <Box className="mb-6 p-4 bg-gray-50 rounded-xl">
          <Box className="flex justify-between mb-2">
            <Typography variant="body2" className="text-gray-600">
              Unit Price:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              ₹{unitPrice} × {quantity}
            </Typography>
          </Box>

          <Box className="flex justify-between mb-2">
            <Typography variant="body2" className="text-gray-600">
              Original Amount:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              ₹{originalAmount}
            </Typography>
          </Box>

          {hasDiscount && (
            <Box className="flex justify-between mb-2">
              <Typography variant="body2" className="text-gray-600">
                Discount:
              </Typography>
              <Typography
                variant="body2"
                className="text-green-600"
                fontWeight="medium"
              >
                -₹{discountAmount}
              </Typography>
            </Box>
          )}

          <Divider className="my-3" />

          <Box className="flex justify-between items-center mt-2">
            <Typography variant="h6" fontWeight="bold">
              Final Amount:
            </Typography>
            <Box className="flex items-center gap-2">
              {hasDiscount && (
                <Chip
                  icon={<DiscountIcon />}
                  label="Discount Applied"
                  color="success"
                  size="small"
                  variant="outlined"
                  className="border-green-500 text-green-700"
                />
              )}
              <Typography
                variant="h6"
                className="text-blue-600"
                fontWeight="bold"
              >
                ₹{discountedAmount}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Payment Methods */}
        <RadioGroup
          value={effectivePaymentMethod}
          onChange={onPaymentMethodChange}
          className="gap-4"
        >
          {/* Online Payment Option */}
          <FormControlLabel
            value="ONLINE"
            control={<Radio className="hidden" />}
            label={
              <Box
                className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  effectivePaymentMethod === "ONLINE"
                    ? "border-blue-500 bg-blue-50 shadow-sm"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                sx={{ width: "100%" }}
              >
                <CardIcon className="text-blue-500 text-3xl mt-1" />
                <Box className="flex-1">
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    className="mb-1"
                  >
                    Pay Now
                  </Typography>

                  {hasDiscount && (
                    <Box className="inline-block bg-green-500 text-white px-2 py-1 rounded-md mb-2">
                      <Typography
                        variant="caption"
                        className="text-white font-bold"
                      >
                        Save ₹{discountAmount}
                      </Typography>
                    </Box>
                  )}

                  <Box className="flex items-center gap-2 mt-2">
                    <Typography variant="body1" fontWeight="bold">
                      ₹{discountedAmount}
                    </Typography>
                    {hasDiscount && (
                      <Typography
                        variant="body2"
                        className="text-gray-500 line-through"
                      >
                        ₹{originalAmount}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            }
            className="m-0 w-full"
          />

          {/* COD Option */}
          {!onlineOnly && (
            <FormControlLabel
              value="COD"
              control={<Radio className="hidden" />}
              label={
                <Box
                  className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    effectivePaymentMethod === "COD"
                      ? "border-blue-500 bg-blue-50 shadow-sm"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  sx={{ width: "100%" }}
                >
                  <CashIcon className="text-blue-500 text-3xl mt-1" />
                  <Box className="flex-1">
                    <Typography
                      variant="subtitle1"
                      fontWeight="medium"
                      className="mb-1"
                    >
                      Pay After Service Completion
                    </Typography>

                    {hasDiscount && (
                      <Typography variant="caption" className="text-gray-500">
                        Standard Price
                      </Typography>
                    )}

                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      className="mt-2"
                    >
                      ₹{originalAmount}
                    </Typography>
                  </Box>
                </Box>
              }
              className="m-0 w-full"
            />
          )}
        </RadioGroup>
      </DialogContent>

      <DialogActions className="p-6 pt-0">
        <Button
          onClick={onSubmit}
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading}
          className="py-3 rounded-xl text-base font-bold shadow-lg hover:shadow-xl transition-all"
        >
          {isLoading
            ? "Processing..."
            : `Confirm ${
                effectivePaymentMethod === "ONLINE" ? "& Pay Now" : "Booking"
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodDialog;
